// ─────────────────────────────────────────────────────────────────────────────
// v2 kit — shared building blocks for the form-heavy pages
// (Create Invoice, Credit Notes, Recurring, Ledger, TDS).
//   Combo       searchable glass picker (clients, products, invoices)
//   LineItems   item rows with product picking + inline stock validation
//   useProducts / useClients  data hooks (tolerant of both response shapes)
//   Stat, PageHead, Field
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import API_BASE from "../apiConfig";
import { Glyph } from "./icons";
import { Layer, inr } from "./primitives";

const auth = () => ({ "auth-token": localStorage.getItem("token") });

// ── Data hooks ───────────────────────────────────────────────────────────────
export function useProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/products`, { headers: auth() });
      const d = await r.json();
      setProducts(Array.isArray(d) ? d : d.products || []);
    } catch { setProducts([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { products, loading, reload: load };
}

export function useClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetch(`${API_BASE}/api/clients/fetchallclients`, { headers: auth() })
      .then((r) => r.json())
      .then((d) => setClients(Array.isArray(d) ? d.filter((c) => c && c.clientName) : []))
      .catch(() => setClients([]))
      .finally(() => setLoading(false));
  }, []);
  return { clients, loading };
}

export const productPrice = (p) => p.sellingPrice ?? p.price ?? 0;
export const productGst = (p, fallback = 0) => p.gstRate ?? p.gst ?? fallback;

// ── Page header ──────────────────────────────────────────────────────────────
export function PageHead({ title, sub, actions, eyebrow }) {
  return (
    <header className="v2p-head">
      <div>
        {eyebrow && <div className="v2k-eyebrow">{eyebrow}</div>}
        <h1 className="v2p-title">{title}</h1>
        {sub && <p className="v2p-sub">{sub}</p>}
      </div>
      {actions && <div className="v2i-head-actions">{actions}</div>}
    </header>
  );
}

export function Field({ label, optional, hint, error, children, className = "" }) {
  return (
    <label className={`v2-field ${className}`}>
      {label && <span className="v2-field-label">{label}{optional && <span className="v2-optional">optional</span>}</span>}
      {children}
      {error ? <span className="v2-field-err">{error}</span> : hint ? <span className="v2-field-hint">{hint}</span> : null}
    </label>
  );
}

export function Stat({ label, value, note, tone, children }) {
  return (
    <div className={`v2k-stat${tone ? ` v2k-stat--${tone}` : ""}`}>
      <span className="v2k-stat-label">{label}</span>
      <span className="v2k-stat-value">{value}</span>
      {note && <span className="v2k-stat-note">{note}</span>}
      {children}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Combo — searchable picker. items: [{ id, label, sub?, right?, disabled?, keywords? }]
// Panel is portaled (never clipped by sheets/cards) and grows from the field.
// ══════════════════════════════════════════════════════════════════════════════
export function Combo({ items, value, onChange, placeholder = "Select…", searchPlaceholder = "Search…",
  renderValue, empty = "Nothing found", invalid, disabled, footer, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const [pos, setPos] = useState(null);
  const btn = useRef(null);
  const panel = useRef(null);
  const listRef = useRef(null);

  const selected = items.find((i) => i.id === value) || null;
  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((i) => `${i.label} ${i.sub || ""} ${i.keywords || ""}`.toLowerCase().includes(s));
  }, [items, q]);

  const place = useCallback(() => {
    if (!btn.current) return;
    const r = btn.current.getBoundingClientRect();
    const vh = window.innerHeight;
    const width = Math.max(r.width, 280);
    const left = Math.min(Math.max(12, r.left), window.innerWidth - width - 12);
    const below = vh - r.bottom;
    const maxH = Math.min(380, Math.max(below, r.top) - 24);
    const up = below < 260 && r.top > below;
    setPos({ left, width, maxH, top: up ? undefined : r.bottom + 8, bottom: up ? vh - r.top + 8 : undefined, origin: up ? "bottom" : "top" });
  }, []);

  useLayoutEffect(() => { if (open) place(); }, [open, place]);
  useEffect(() => {
    if (!open) return;
    setQ(""); setHi(Math.max(0, items.findIndex((i) => i.id === value)));
    const onDown = (e) => { if (!panel.current?.contains(e.target) && !btn.current?.contains(e.target)) setOpen(false); };
    const onScroll = (e) => { if (!panel.current?.contains(e.target)) place(); };
    document.addEventListener("pointerdown", onDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", place);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", place);
    };
  }, [open]); // eslint-disable-line
  useEffect(() => { setHi(0); }, [q]);
  useEffect(() => { listRef.current?.querySelector(`[data-i="${hi}"]`)?.scrollIntoView({ block: "nearest" }); }, [hi]);

  const pick = (it) => { if (it.disabled) return; onChange(it.id, it); setOpen(false); btn.current?.focus(); };

  return (
    <>
      <button ref={btn} type="button" className={`v2k-combo${invalid ? " is-bad" : ""}${open ? " is-open" : ""}`}
        aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel} disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if ((e.key === "ArrowDown" || e.key === "Enter") && !open) { e.preventDefault(); setOpen(true); } }}>
        <span className="v2k-combo-value">
          {selected ? (renderValue ? renderValue(selected) : selected.label) : <span className="v2k-combo-ph">{placeholder}</span>}
        </span>
        <Glyph name="chevronDown" size={16} stroke={2} className="v2k-combo-chev" />
      </button>
      {open && pos && (
        <Layer>
          <div ref={panel} className="v2k-combo-panel" role="listbox"
            style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxH, transformOrigin: `50% ${pos.origin}` }}>
            <div className="v2k-combo-search">
              <Glyph name="search" size={16} />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchPlaceholder}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length - 1)); }
                  if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
                  if (e.key === "Enter") { e.preventDefault(); if (results[hi]) pick(results[hi]); }
                  if (e.key === "Escape") { setOpen(false); btn.current?.focus(); }
                }} />
            </div>
            <div ref={listRef} className="v2k-combo-list">
              {results.length === 0 && <div className="v2k-combo-empty">{empty}</div>}
              {results.map((it, i) => (
                <button key={it.id} type="button" data-i={i} role="option" aria-selected={it.id === value}
                  className={`v2k-opt${i === hi ? " is-hi" : ""}${it.id === value ? " is-sel" : ""}${it.disabled ? " is-off" : ""}`}
                  onPointerEnter={() => setHi(i)} onClick={() => pick(it)}>
                  <span className="v2k-opt-main">
                    <span className="v2k-opt-label">{it.label}</span>
                    {it.sub && <span className="v2k-opt-sub">{it.sub}</span>}
                  </span>
                  {it.right && <span className="v2k-opt-right">{it.right}</span>}
                  {it.id === value && <Glyph name="check" size={15} stroke={2.4} className="v2k-opt-check" />}
                </button>
              ))}
            </div>
            {footer && <div className="v2k-combo-foot" onClick={() => setOpen(false)}>{footer}</div>}
          </div>
        </Layer>
      )}
    </>
  );
}

export function StockPill({ stock, reorder }) {
  const s = Number(stock ?? 0);
  if (s <= 0) return <span className="v2k-stock is-out">Out of stock</span>;
  if (s <= (reorder || 0)) return <span className="v2k-stock is-low">Low · {s}</span>;
  return <span className="v2k-stock">{s} in stock</span>;
}

// ══════════════════════════════════════════════════════════════════════════════
// LineItems — editable item rows.
//   items: [{ key, productId, name, hsnCode, unit, price, qty, gstRate, batchNo?, expiryDate? }]
//   opts: { stock: validate qty against product stock, goods: show batch/expiry,
//           defaultGst: rate when a product has none, amountWithGst: show amount incl. GST }
// ══════════════════════════════════════════════════════════════════════════════
let keySeq = 1;
export const newLine = (patch = {}) => ({
  key: `l${keySeq++}`, productId: null, name: "", hsnCode: "", unit: "pcs", price: "", qty: 1, gstRate: 18, batchNo: "", expiryDate: "", ...patch,
});

export const GST_RATES = [0, 5, 12, 18, 28];
const UNITS = ["pcs", "nos", "set", "kg", "g", "m", "cm", "l", "ml", "hr", "day", "box", "ctn"];

export function LineItems({ items, onChange, products = [], productsLoading, stock = false, goods = false,
  defaultGst = 0, amountWithGst = false, errors = {}, compact = false, priceFor = productPrice }) {
  const byId = useMemo(() => Object.fromEntries(products.map((p) => [p._id, p])), [products]);
  const productItems = useMemo(() => [
    { id: "__custom", label: "Custom item", sub: "Type a name, HSN/SAC and rate", right: <Glyph name="plus" size={15} stroke={2.2} /> },
    ...products.map((p) => ({
      id: p._id, label: p.name,
      sub: [p.sku, p.hsnCode && `HSN ${p.hsnCode}`, inr(priceFor(p), { decimals: 2 })].filter(Boolean).join(" · "),
      right: stock ? <StockPill stock={p.currentStock} reorder={p.reorderLevel} /> : null,
      keywords: `${p.sku || ""} ${p.hsnCode || ""}`,
    })),
  ], [products, stock, priceFor]);

  const set = (key, patch) => onChange(items.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  const remove = (key) => onChange(items.filter((it) => it.key !== key));
  const pickProduct = (key, id) => {
    if (id === "__custom") { set(key, { productId: null, custom: true }); return; }
    const p = byId[id];
    if (!p) return;
    set(key, {
      productId: p._id, custom: false, name: p.name, hsnCode: p.hsnCode || "", unit: p.unit || "pcs",
      price: priceFor(p), gstRate: Number(productGst(p, defaultGst)),
    });
  };

  return (
    <div className={`v2k-lines${compact ? " is-compact" : ""}`}>
      {items.length > 0 && (
        <div className="v2k-line-head" aria-hidden="true">
          <span>Item</span><span>Qty</span><span>Rate</span><span>GST</span><span>Amount</span><span />
        </div>
      )}
      {items.map((it, idx) => {
        const p = it.productId ? byId[it.productId] : null;
        const qty = Number(it.qty || 0);
        const overStock = stock && p && qty > Number(p.currentStock ?? 0);
        const base = Number(it.price || 0) * qty;
        const amount = amountWithGst ? base * (1 + Number(it.gstRate || 0) / 100) : base;
        const err = errors[it.key];
        return (
          <div key={it.key} className={`v2k-line${overStock || err ? " is-bad" : ""}`}>
            <div className="v2k-line-item">
              {it.productId || !it.custom ? (
                <Combo items={productItems} value={it.productId || null} onChange={(id) => pickProduct(it.key, id)}
                  placeholder={productsLoading ? "Loading products…" : products.length ? "Choose a product…" : "Choose or add an item…"}
                  searchPlaceholder="Search products, SKU or HSN" ariaLabel={`Item ${idx + 1}`}
                  renderValue={(o) => <span className="v2k-line-picked">{o.label}</span>} invalid={!!err && !it.name} />
              ) : (
                <input className="v2-input v2k-line-name" value={it.name} placeholder="Item or service name" autoFocus
                  onChange={(e) => set(it.key, { name: e.target.value })} aria-label={`Item ${idx + 1} name`} />
              )}
              <div className="v2k-line-meta">
                {(it.custom && !it.productId) ? (
                  <>
                    <input className="v2k-mini" value={it.hsnCode} placeholder="HSN / SAC" onChange={(e) => set(it.key, { hsnCode: e.target.value })} aria-label="HSN or SAC" />
                    <select className="v2k-mini" value={it.unit} onChange={(e) => set(it.key, { unit: e.target.value })} aria-label="Unit">
                      {UNITS.map((u) => <option key={u}>{u}</option>)}
                    </select>
                    <button type="button" className="v2k-link" onClick={() => set(it.key, { custom: false, name: "", hsnCode: "" })}>Pick from inventory</button>
                  </>
                ) : p ? (
                  <>
                    {p.hsnCode && <span>HSN {p.hsnCode}</span>}
                    <span>{p.unit || "pcs"}</span>
                    {stock && <StockPill stock={p.currentStock} reorder={p.reorderLevel} />}
                  </>
                ) : null}
                {goods && (
                  <>
                    <input className="v2k-mini" value={it.batchNo || ""} placeholder="Batch" onChange={(e) => set(it.key, { batchNo: e.target.value })} aria-label="Batch number" />
                    <input className="v2k-mini" type="date" value={it.expiryDate || ""} onChange={(e) => set(it.key, { expiryDate: e.target.value })} aria-label="Expiry date" title="Expiry" />
                  </>
                )}
              </div>
              {overStock && <span className="v2-field-err">Only {p.currentStock} in stock</span>}
              {err && !overStock && <span className="v2-field-err">{err}</span>}
            </div>
            <label className="v2k-line-num" data-label="Qty">
              <input className="v2-input" type="number" min="0" step="any" inputMode="decimal" value={it.qty}
                onChange={(e) => set(it.key, { qty: e.target.value })} aria-label="Quantity" />
            </label>
            <label className="v2k-line-num" data-label="Rate">
              <span className="v2k-cur">₹</span>
              <input className="v2-input v2k-rate" type="number" min="0" step="0.01" inputMode="decimal" value={it.price}
                onChange={(e) => set(it.key, { price: e.target.value })} aria-label="Rate" />
            </label>
            <label className="v2k-line-num" data-label="GST">
              <select className="v2-input" value={it.gstRate} onChange={(e) => set(it.key, { gstRate: Number(e.target.value) })} aria-label="GST rate">
                {[...new Set([...GST_RATES, Number(it.gstRate)])].sort((a, b) => a - b).map((r) => <option key={r} value={r}>{r}%</option>)}
              </select>
            </label>
            <span className="v2k-line-amt" data-label="Amount">{inr(amount, { decimals: 2 })}</span>
            <button type="button" className="v2-icon-btn v2k-line-del" aria-label={`Remove item ${idx + 1}`} onClick={() => remove(it.key)}>
              <Glyph name="close" size={15} stroke={2} />
            </button>
          </div>
        );
      })}
      <div className="v2k-line-add">
        <button type="button" className="v2-btn v2-btn--sm" onClick={() => onChange([...items, newLine({ gstRate: defaultGst || 0 })])}>
          <Glyph name="plus" size={15} stroke={2.2} /> Add item
        </button>
        <button type="button" className="v2-btn v2-btn--sm v2k-ghost" onClick={() => onChange([...items, newLine({ custom: true, gstRate: defaultGst || 0 })])}>
          Custom line
        </button>
      </div>
    </div>
  );
}

// Product-backed lines whose qty exceeds stock (for submit validation)
export function stockProblems(items, products) {
  const byId = Object.fromEntries(products.map((p) => [p._id, p]));
  return items.filter((it) => it.productId && byId[it.productId] && Number(it.qty) > Number(byId[it.productId].currentStock ?? 0))
    .map((it) => `${it.name}: only ${byId[it.productId].currentStock} in stock`);
}

export const purchasePrice = (p) => p.purchasePrice ?? 0;
// Lines as v1 purchase/PO items: { name, hsnCode, unit, qty, price, gstRate, amount, productId? }
export const toPurchaseItems = (lines) => lines.filter((l) => l.productId || l.name?.trim()).map((l) => {
  const qty = parseFloat(l.qty) || 0; const price = parseFloat(l.price) || 0;
  return { name: l.name, hsnCode: l.hsnCode || "", unit: l.unit || "pcs", qty, price, gstRate: Number(l.gstRate) || 0, amount: qty * price, ...(l.productId ? { productId: l.productId } : {}) };
});
export const fromPurchaseItems = (items = [], fallbackGst = 18) => items.map((it) => newLine({
  productId: it.productId?._id || it.productId || null, custom: !it.productId, name: it.name || "", hsnCode: it.hsnCode || "",
  unit: it.unit || "pcs", qty: it.qty || 1, price: it.price ?? 0, gstRate: it.gstRate ?? fallbackGst,
}));
// v1 purchase/PO totals: GST added per line on (qty × price)
export const purchaseTotals = (lines, supplyType = "intra") => {
  const items = toPurchaseItems(lines);
  const subtotal = items.reduce((s, i) => s + i.amount, 0);
  const tax = items.reduce((s, i) => s + (i.amount * i.gstRate) / 100, 0);
  return { subtotal, tax, cgst: supplyType === "intra" ? tax / 2 : 0, sgst: supplyType === "intra" ? tax / 2 : 0, igst: supplyType === "inter" ? tax : 0, total: subtotal + tax };
};
